import React, { useState, useEffect } from "react";
import {
  Save, Store, Mail, Phone, MapPin,
  DollarSign, Percent, Bell, Shield, Image as ImageIcon, CheckCircle,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import MediaPicker from "../MediaPicker";

const CURRENCIES = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "INR", label: "INR (₹)" },
  { value: "AUD", label: "AUD (A$)" },
  { value: "CAD", label: "CAD (C$)" },
  { value: "AED", label: "AED (د.إ)" },
  { value: "JPY", label: "JPY (¥)" },
];

const SettingsPage = () => {
  const { storeSettings, saveSettings, showToast } = useAdmin();

  const [formData, setFormData] = useState({
    store_name:          "Gamapa Foods",
    store_email:         "",
    store_phone:         "",
    store_currency:      "USD",
    tax_rate:            5.0,
    low_stock_threshold: 10,
    store_address:       "",
    store_logo:          null,
    notifications: { orderEmail: true, lowStockEmail: true, urgentSms: false },
  });

  const [saving, setSaving] = useState(false);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  // Sync form when settings load from backend
  useEffect(() => {
    if (!storeSettings) return;
    setFormData({
      store_name:          storeSettings.store_name          || "Gamapa Foods",
      store_email:         storeSettings.store_email         || "",
      store_phone:         storeSettings.store_phone         || "",
      store_currency:      storeSettings.store_currency      || "USD",
      tax_rate:            storeSettings.tax_rate            ?? 5.0,
      low_stock_threshold: storeSettings.low_stock_threshold ?? 10,
      store_address:       storeSettings.store_address       || "",
      store_logo:          storeSettings.store_logo          || null,
      notifications:       storeSettings.notifications       || { orderEmail: true, lowStockEmail: true, urgentSms: false },
    });
  }, [storeSettings]);

  const field = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const handleNotif = (k) =>
    setFormData((p) => ({ ...p, notifications: { ...p.notifications, [k]: !p.notifications[k] } }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await saveSettings(formData);
    setSaving(false);
    if (result?.status) showToast("Settings saved successfully!", "success");
    else showToast("Failed to save settings.", "error");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Settings</h1>
          <p className="text-gray-500 text-sm">Manage your store details and configurations.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95"
        >
          <Save size={20} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* Section 1 — General Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg text-orange-600"><Store size={20} /></div>
            <h3 className="font-bold text-gray-800">General Information</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Store Name */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Store Name</label>
              <div className="relative">
                <Store className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="store_name" type="text" value={formData.store_name}
                  onChange={(e) => field("store_name", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Store Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="store_email" type="email" value={formData.store_email}
                  onChange={(e) => field("store_email", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="store_phone" type="tel" value={formData.store_phone}
                  onChange={(e) => field("store_phone", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
              </div>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Currency</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <select name="store_currency" value={formData.store_currency}
                  onChange={(e) => field("store_currency", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition bg-white appearance-none">
                  {CURRENCIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">Store Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="store_address" type="text" value={formData.store_address}
                  onChange={(e) => field("store_address", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2 — Configuration + Logo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Shield size={20} /></div>
              <h3 className="font-bold text-gray-800">Configuration</h3>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tax Rate (%)</label>
                <div className="relative">
                  <Percent className="absolute left-3 top-2.5 text-gray-400" size={18} />
                  <input name="tax_rate" type="number" step="0.1" value={formData.tax_rate}
                    onChange={(e) => field("tax_rate", parseFloat(e.target.value))}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Low Stock Threshold</label>
                <input name="low_stock_threshold" type="number" value={formData.low_stock_threshold}
                  onChange={(e) => field("low_stock_threshold", parseInt(e.target.value))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition" />
                <p className="text-xs text-gray-500 mt-1">Products below this amount will be flagged.</p>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg text-purple-600"><ImageIcon size={20} /></div>
              <h3 className="font-bold text-gray-800">Store Logo</h3>
            </div>
            <div className="p-6 flex flex-col items-center justify-center gap-4">
              <div className="w-32 h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center overflow-hidden">
                {formData.store_logo
                  ? <img src={formData.store_logo} alt="Logo" className="w-full h-full object-contain p-2" />
                  : <span className="text-gray-400 font-bold text-2xl">LOGO</span>}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setLogoPickerOpen(true)}
                  className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold py-2 px-4 rounded-xl flex items-center gap-2 transition shadow-sm text-sm"
                >
                  <ImageIcon size={16} />
                  {formData.store_logo ? "Change Logo" : "Pick from Media Library"}
                </button>
                {formData.store_logo && (
                  <button
                    type="button"
                    onClick={() => field("store_logo", null)}
                    className="bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200 text-gray-400 hover:text-red-400 font-bold py-2 px-3 rounded-xl transition shadow-sm text-sm"
                    title="Remove logo"
                  >✕</button>
                )}
              </div>
              <p className="text-xs text-gray-400 text-center">Upload your logo to the Media Library first,<br />then pick it here.</p>
            </div>
            {logoPickerOpen && (
              <MediaPicker
                onSelect={({ url }) => { field("store_logo", url); setLogoPickerOpen(false); }}
                onClose={() => setLogoPickerOpen(false)}
              />
            )}
          </div>
        </div>

        {/* Section 3 — Notifications */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-lg text-green-600"><Bell size={20} /></div>
            <h3 className="font-bold text-gray-800">Notification Preferences</h3>
          </div>
          <div className="p-6 space-y-4">
            {[
              { key: "orderEmail",   label: "Email notifications for new orders"   },
              { key: "lowStockEmail", label: "Email notifications for low stock"    },
              { key: "urgentSms",    label: "SMS notifications for urgent orders"  },
            ].map(({ key, label }) => (
              <label key={key} onClick={() => handleNotif(key)}
                className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${formData.notifications[key] ? "bg-orange-500 border-orange-500" : "border-gray-300"}`}>
                    {formData.notifications[key] && <CheckCircle size={14} className="text-white" />}
                  </div>
                  <span className="font-medium text-gray-700">{label}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

      </form>
    </div>
  );
};

export default SettingsPage;
